import type { AgentChatEntry, AnswerContactRequest, ApprovalChatEntry, ChatEntry, ContactChatEntry, HandoffChatEntry, HandoffResolution, PeerChatEntry, RoomChatEntry, UserChatEntry } from "@ark-bot/protocol";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Markdown from "react-markdown";

import { api } from "../api.js";
import { useBackClose } from "../back.js";
import { followsArrival, nearBottom } from "../chat-scroll.js";
import { stampBefore } from "../chat-time.js";
import { copyTextOf, findQuote, SWIPE_REPLY_PX, swipePull, swipeStarts, type Quote } from "../reply.js";
import { LONG_PRESS_MS, menuOpensFrom, PRESS_SLOP_PX, wandered } from "../row-gesture.js";
import { formatLocale, locale, useText } from "../i18n.js";
import { displayDateTime, displayTime, useTimeZone } from "../time-zone.js";
import { splitMentions, type Mentionable } from "../mentions.js";
import { asEntry, type Outgoing } from "../outbox.js";
import { usePresence } from "../presence.js";
import type { AgentStatus } from "../state.js";
import { CHAT } from "../text/chat.js";

import { ContextMenu, type Divider, type MenuItem, type MenuState } from "./ContextMenu.js";
import { FormCard } from "./FormCard.js";
import { LoginCard } from "./LoginCard.js";
import type { SubmitLoginRequest } from "@ark-bot/protocol";
import { Lightbox } from "./Lightbox.js";

/** Draws an agent's face by id, resolved by the parent because only it holds the roster. */
export type AvatarFor = (id: string, name: string, size?: number) => React.JSX.Element;

/** A picture in the chat, opened large over the page rather than in a new tab. */
export interface Picture {
  readonly src: string;
  readonly alt: string;
  readonly name?: string;
}
type ViewPicture = (picture: Picture) => void;

type Words = (typeof CHAT)["en"];

/** A number with this many decimals: as English always wrote it, or with Indonesian's decimal comma. */
function fixed(value: number, digits: number): string {
  return locale() === "id" ? value.toLocaleString(formatLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }) : value.toFixed(digits);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${fixed(bytes / 1024, bytes < 10_240 ? 1 : 0)} KB`;
  return `${fixed(bytes / (1024 * 1024), 1)} MB`;
}

function timeOf(entry: ChatEntry): string {
  return displayTime(entry.createdAt);
}

/** A picture in a bubble: pressing it opens it large. */
function ImageButton({ src, alt, name, onView }: { readonly src: string; readonly alt: string; readonly name?: string; readonly onView: ViewPicture }): React.JSX.Element {
  const t = useText(CHAT);
  return (
    <button type="button" className="image-button" onClick={() => onView(name == null ? { src, alt } : { src, alt, name })} title={alt.length > 0 ? alt : t.openPicture}>
      <img src={src} alt={alt} />
    </button>
  );
}

/** Opens another agent's chat, from a mention of it. */
export type OpenAgent = (agentId: string) => void;

/**
 * What the user typed, with the @-mentions the host will read picked out, so
 * they can see which ones took. A mention of an agent wears its face where
 * the @ was and opens its chat; one of the whole room has nowhere to go and
 * stays a label.
 */
function MentionText({ text, mentionables, avatarFor, onOpenAgent }: { readonly text: string; readonly mentionables: readonly Mentionable[]; readonly avatarFor: AvatarFor; readonly onOpenAgent: OpenAgent }): React.JSX.Element {
  const t = useText(CHAT);
  return (
    <>
      {splitMentions(text, mentionables).map((segment, index) => {
        if (!segment.mention) return segment.text;
        const row = segment.row;
        if (row == null || row.everyone === true) {
          return (
            <span key={index} className="mention">
              {segment.text}
            </span>
          );
        }
        return (
          <button key={index} type="button" className="mention" onClick={() => onOpenAgent(row.id)} title={t.openAgent(row.name)} aria-label={t.mentionOpens(segment.text, row.name)}>
            <span className="mention-face">{avatarFor(row.id, row.name, 16)}</span>
            {segment.text.slice(1)}
          </button>
        );
      })}
    </>
  );
}

/** The user chose to answer one message: its id, who said it and a preview, for the composer's chip. */
export type ReplyTo = (target: { readonly id: string; readonly author: string; readonly preview: string }) => void;

/** Opens a message's menu at a point on the screen: its ⋯ button, a right click, or a long press. */
type OpenMenu = (x: number, y: number) => void;

/** The reply arrow, drawn: a glyph sits wherever each style's font puts it. */
function ReplyArrow({ size }: { readonly size: number }): React.JSX.Element {
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} aria-hidden="true">
      <path d="M8 5 4 9l4 4M4 9h7.5a4.5 4.5 0 0 1 4.5 4.5V15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * What a finger or a mouse does to a message, as a phone's messenger has it.
 *
 * - A right click, or a long press on a touch screen, opens its menu. The
 *   press opens it under the bubble, where the ⋯ would, so the finger does
 *   not cover it. A finger's press raises Android's own `contextmenu` as
 *   well; that one is left alone, so the menu opens once (row-gesture.ts).
 * - Pulling the message to the right answers it, as WhatsApp's swipe: it
 *   follows the finger, the arrow shows behind it, and letting go past the
 *   reply point picks it. A move more down than across is the chat
 *   scrolling, and the row's `touch-action: pan-y` leaves that to the page.
 */
function useMessagePress(open: OpenMenu | undefined, reply: (() => void) | null): React.HTMLAttributes<HTMLDivElement> {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const mode = useRef<"pending" | "swipe" | "done">("done");
  const pointer = useRef<string | null>(null);
  if (open == null) return {};
  const stopTimer = (): void => {
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = null;
  };
  const release = (row: HTMLElement): void => {
    stopTimer();
    start.current = null;
    mode.current = "done";
    row.classList.remove("held", "swiping", "swipe-armed");
    row.style.removeProperty("--swipe");
  };
  return {
    onPointerDown: (event) => {
      pointer.current = event.pointerType;
    },
    onContextMenu: (event) => {
      // A link or a picture keeps the browser's own menu: saving and opening those is what it is for.
      if ((event.target as HTMLElement).closest("a, img") != null) return;
      event.preventDefault();
      if (menuOpensFrom(pointer.current)) open(event.clientX, event.clientY);
    },
    onTouchStart: (event) => {
      const touch = event.touches[0];
      const row = event.currentTarget;
      if (touch == null || event.touches.length > 1) {
        release(row);
        return;
      }
      release(row);
      start.current = { x: touch.clientX, y: touch.clientY };
      mode.current = "pending";
      // A picture or a link keeps its own long press, as the right click does.
      if ((event.target as HTMLElement).closest("a, img") != null) return;
      row.classList.add("held");
      timer.current = setTimeout(() => {
        timer.current = null;
        mode.current = "done";
        row.classList.remove("held");
        navigator.vibrate?.(12);
        const box = (row.querySelector(".bubble") ?? row).getBoundingClientRect();
        open(box.left, box.bottom + 4);
      }, LONG_PRESS_MS);
    },
    onTouchMove: (event) => {
      const touch = event.touches[0];
      const from = start.current;
      const row = event.currentTarget;
      if (touch == null || from == null) return;
      const dx = touch.clientX - from.x;
      const dy = touch.clientY - from.y;
      if (mode.current === "pending") {
        if (!wandered(from, { x: touch.clientX, y: touch.clientY })) return;
        stopTimer();
        row.classList.remove("held");
        if (reply == null || !swipeStarts(dx, dy)) {
          mode.current = "done";
          return;
        }
        // Counted from here, so the message does not jump by the slop it waited out.
        start.current = { x: from.x + PRESS_SLOP_PX, y: from.y };
        mode.current = "swipe";
        row.classList.add("swiping");
        return;
      }
      if (mode.current !== "swipe") return;
      const pull = swipePull(touch.clientX - from.x);
      row.style.setProperty("--swipe", `${pull}px`);
      const armed = pull >= SWIPE_REPLY_PX;
      if (armed !== row.classList.contains("swipe-armed")) {
        row.classList.toggle("swipe-armed", armed);
        if (armed) navigator.vibrate?.(8);
      }
    },
    onTouchEnd: (event) => {
      const row = event.currentTarget;
      const answer = mode.current === "swipe" && row.classList.contains("swipe-armed") ? reply : null;
      release(row);
      answer?.();
    },
    onTouchCancel: (event) => release(event.currentTarget),
  };
}

/** Behind a message on a phone: the arrow its swipe to the right uncovers. */
function SwipeReply(): React.JSX.Element {
  return (
    <span className="swipe-reply" aria-hidden="true">
      <ReplyArrow size={16} />
    </span>
  );
}

/** Beside a bubble on hover: reply in one press, the ⋯ that opens its menu, and when it was sent. */
function MessageActions({ entry, onMenu, onReply }: { readonly entry: ChatEntry; readonly onMenu: OpenMenu; readonly onReply: (() => void) | null }): React.JSX.Element {
  const t = useText(CHAT);
  return (
    <div className="message-actions">
      {onReply != null ? (
        <button type="button" className="message-reply" onClick={onReply} title={t.reply} aria-label={t.reply}>
          <ReplyArrow size={15} />
        </button>
      ) : null}
      <button
        type="button"
        className="message-more"
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          onMenu(box.left, box.bottom + 4);
        }}
        title={t.messageOptions}
        aria-label={t.messageOptions}
        aria-haspopup="menu"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
          <circle cx="4.5" cy="10" r="1.6" fill="currentColor" />
          <circle cx="10" cy="10" r="1.6" fill="currentColor" />
          <circle cx="15.5" cy="10" r="1.6" fill="currentColor" />
        </svg>
      </button>
      <time className="message-time" dateTime={new Date(entry.createdAt).toISOString()}>
        {timeOf(entry)}
      </time>
    </div>
  );
}

/** What a quoted message stood for when it had no words: a picture, or a file by its name. */
function quoteWords(quote: Quote, t: Words): string {
  if (quote.text.length > 0) return quote.text;
  if (quote.media === "picture") return t.quotedPicture;
  if (quote.media === "file") return quote.fileName ?? t.quotedFile;
  return "";
}

/**
 * The message this one answers, over its text as WhatsApp quotes one: who
 * said it and the start of what they said. Pressing it scrolls to that
 * message. One further back than the loaded page is only named as earlier.
 */
function ReplyQuote({ quote, agentName, onJump }: { readonly quote: Quote | null; readonly agentName: string; readonly onJump: (id: string) => void }): React.JSX.Element {
  const t = useText(CHAT);
  if (quote == null) return <div className="reply-quote missing">{t.repliedToEarlier}</div>;
  const who = quote.from === "user" ? t.you : (quote.author ?? agentName);
  return (
    <button type="button" className={`reply-quote from-${quote.from}`} onClick={() => onJump(quote.id)} title={t.showRepliedMessage}>
      <span className="reply-quote-who">{who}</span>
      <span className="reply-quote-text">{quoteWords(quote, t)}</span>
    </button>
  );
}

/** What each bubble is handed to quote the message it answers and to open its own menu. */
interface Threading {
  readonly quoteFor: (id: string) => Quote | null;
  readonly agentName: string;
  readonly onJump: (id: string) => void;
  readonly onMenu?: ((entry: ChatEntry, x: number, y: number) => void) | undefined;
  /** Picks this message as the one the next answers; null where it cannot be. */
  readonly replyFor?: ((entry: ChatEntry) => (() => void) | null) | undefined;
}

function UserBubble({ entry, mentionables, avatarFor, onView, onOpenAgent, threading }: { readonly entry: UserChatEntry; readonly mentionables: readonly Mentionable[]; readonly avatarFor: AvatarFor; readonly onView: ViewPicture; readonly onOpenAgent: OpenAgent; readonly threading: Threading }): React.JSX.Element {
  const t = useText(CHAT);
  const onMenu = threading.onMenu;
  const open = onMenu == null ? undefined : (x: number, y: number) => onMenu(entry, x, y);
  const reply = open == null ? null : (threading.replyFor?.(entry) ?? null);
  const press = useMessagePress(open, reply);
  return (
    <div className="bubble-row user" {...press}>
      <div className="bubble user" title={timeOf(entry)}>
        {entry.replyTo != null ? <ReplyQuote quote={threading.quoteFor(entry.replyTo)} agentName={threading.agentName} onJump={threading.onJump} /> : null}
        {entry.attachments.length > 0 ? (
          <div className="bubble-images">
            {entry.attachments.map((attachment) => (
              <ImageButton key={attachment.blobId} src={api.blobUrl(attachment.blobId)} alt="" name={t.attachment} onView={onView} />
            ))}
          </div>
        ) : null}
        {entry.text.length > 0 ? (
          <div className="bubble-text">
            <MentionText text={entry.text} mentionables={mentionables} avatarFor={avatarFor} onOpenAgent={onOpenAgent} />
          </div>
        ) : null}
        {entry.via === "whatsapp" ? <div className="bubble-via">{t.fromWhatsApp}</div> : null}
        {entry.reactions != null && entry.reactions.length > 0 ? (
          <div className="reactions">
            {entry.reactions.map((reaction, index) => (
              <span key={index} className={`reaction reaction-${reaction.by}`} title={reaction.by === "agent" ? t.botReacted : t.youReacted}>
                {reaction.emoji}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {reply != null ? <SwipeReply /> : null}
      {open != null ? <MessageActions entry={entry} onMenu={open} onReply={reply} /> : null}
    </div>
  );
}

/**
 * The agent's bubble. In a chat with one agent the prototype draws no face
 * beside it — the whole left column is that agent — so only the first of a
 * run gets a little extra room.
 */
function AgentBubble({ entry, previous, onView, threading }: { readonly entry: AgentChatEntry; readonly previous: ChatEntry | undefined; readonly onView: ViewPicture; readonly threading: Threading }): React.JSX.Element {
  const file = entry.file;
  const fileIsImage = file != null && file.mimeType.startsWith("image/");
  const first = previous == null || previous.kind !== "agent";
  const t = useText(CHAT);
  const onMenu = threading.onMenu;
  const open = onMenu == null ? undefined : (x: number, y: number) => onMenu(entry, x, y);
  const reply = open == null ? null : (threading.replyFor?.(entry) ?? null);
  const press = useMessagePress(open, reply);
  return (
    <div className={`bubble-row agent${first ? " first" : ""}`} {...press}>
      <div className="bubble agent" title={timeOf(entry)}>
        {entry.replyTo != null ? <ReplyQuote quote={threading.quoteFor(entry.replyTo)} agentName={threading.agentName} onJump={threading.onJump} /> : null}
        {entry.text != null && entry.text.length > 0 ? (
          <div className="bubble-text markdown">
            <Markdown>{entry.text}</Markdown>
          </div>
        ) : null}
        {entry.images != null && entry.images.length > 0 ? (
          <div className={`bubble-images${entry.images.length > 1 ? " gallery" : ""}`}>
            {entry.images.map((image) => (
              <ImageButton key={image.blobId} src={api.blobUrl(image.blobId)} alt={image.alt ?? ""} onView={onView} />
            ))}
          </div>
        ) : null}
        {file != null ? (
          fileIsImage ? (
            <div className="bubble-images">
              <ImageButton src={api.blobUrl(file.blobId)} alt="" name={file.name} onView={onView} />
            </div>
          ) : (
            <a className="file-card" href={api.blobUrl(file.blobId)} target="_blank" rel="noreferrer" download={file.name}>
              <span className="file-name">{file.name}</span>
              <span className="file-meta">
                {file.mimeType} · {formatSize(file.size)}
              </span>
            </a>
          )
        ) : null}
        {entry.via === "whatsapp" ? <div className="bubble-via">{t.alsoSentToPhone}</div> : null}
      </div>
      {reply != null ? <SwipeReply /> : null}
      {open != null ? <MessageActions entry={entry} onMenu={open} onReply={reply} /> : null}
    </div>
  );
}

export type AnswerContact = (entryId: string, request: AnswerContactRequest) => Promise<void>;

const CONTACT_LABEL: Record<ContactChatEntry["status"], (t: Words) => string> = { open: (t) => t.waitingForYou, answered: (t) => t.contactAnswered, dismissed: (t) => t.contactSetAside };

/**
 * Someone on WhatsApp, passed on by the agent (§6.19). The answer typed here
 * goes to that person's conversation only, where the agent passes it on.
 */
const MEETING_LABEL: Record<ContactChatEntry["status"], (t: Words) => string> = { open: (t) => t.meetingHolding, answered: (t) => t.meetingBooked, dismissed: (t) => t.meetingDeclined };

/** "Thu 17 Sep · 16:00–16:30", in the zone the page shows. */
function meetingWhen(meeting: NonNullable<ContactChatEntry["meeting"]>, zone: string): string {
  const day = new Date(meeting.start).toLocaleDateString(formatLocale("en-GB"), { timeZone: zone, weekday: "short", day: "numeric", month: "short" });
  const clock = (at: number): string => new Date(at).toLocaleTimeString(formatLocale("en-GB"), { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${day} · ${clock(meeting.start)}–${clock(meeting.end)}`;
}

/**
 * A time someone picked from the user's free times, held until the user books
 * it into their calendar or declines it. Either way the agent tells them,
 * with the user's word if they add one.
 */
function MeetingCard({ entry, meeting, onAnswer }: { readonly entry: ContactChatEntry; readonly meeting: NonNullable<ContactChatEntry["meeting"]>; readonly onAnswer: AnswerContact }): React.JSX.Element {
  const { zone } = useTimeZone();
  const t = useText(CHAT);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = entry.contact.number == null ? entry.contact.name : `${entry.contact.name} · +${entry.contact.number}`;
  const send = async (decision: "book" | "decline"): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onAnswer(entry.id, { meeting: decision, ...(message.trim().length > 0 ? { message: message.trim() } : {}) });
      setMessage("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`bubble-row contact contact-${entry.status}`}>
      <div className="contact-card meeting" title={timeOf(entry)}>
        <div className="contact-head">
          <span className="contact-label">{t.whatsAppMeeting}</span>
          <span className="contact-status">{MEETING_LABEL[entry.status](t)}</span>
        </div>
        <div className="contact-who">{who}</div>
        <div className="contact-when">{meetingWhen(meeting, zone)}</div>
        <div className="contact-text">{meeting.topic}</div>
        {entry.status !== "open" && entry.answer != null ? (
          <div className="contact-answer">
            <span className="muted">{t.youSaid}</span> {entry.answer}
          </div>
        ) : null}
        {entry.status === "answered" && entry.eventLink != null ? (
          <a className="contact-link" href={entry.eventLink} target="_blank" rel="noreferrer">
            {t.openInCalendar}
          </a>
        ) : null}
        {entry.status === "open" ? (
          <form
            className="contact-reply"
            onSubmit={(event) => {
              event.preventDefault();
              if (!busy) void send("book");
            }}
          >
            <input value={message} onChange={(event) => setMessage(event.target.value)} placeholder={t.meetingWordPlaceholder} aria-label={t.meetingWordLabel(entry.contact.name)} maxLength={2000} />
            <button type="submit" className="primary" disabled={busy}>
              {busy ? t.booking : t.bookIt}
            </button>
            <button type="button" className="quiet" disabled={busy} onClick={() => void send("decline")}>
              {t.decline}
            </button>
          </form>
        ) : null}
        {error != null ? <div className="contact-error">{error}</div> : null}
      </div>
    </div>
  );
}

function ContactCard({ entry, onAnswer }: { readonly entry: ContactChatEntry; readonly onAnswer: AnswerContact }): React.JSX.Element {
  const t = useText(CHAT);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  if (entry.mode === "meeting" && entry.meeting != null) return <MeetingCard entry={entry} meeting={entry.meeting} onAnswer={onAnswer} />;
  const who = entry.contact.number == null ? entry.contact.name : `${entry.contact.name} · +${entry.contact.number}`;
  const send = async (request: AnswerContactRequest): Promise<void> => {
    setBusy(true);
    try {
      await onAnswer(entry.id, request);
      setAnswer("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`bubble-row contact contact-${entry.status}`}>
      <div className={`contact-card${entry.urgent === true ? " urgent" : ""}`} title={timeOf(entry)}>
        <div className="contact-head">
          <span className="contact-label">
            WhatsApp · {entry.mode === "question" ? t.contactQuestion : t.contactMessage}
            {entry.urgent === true ? t.contactUrgent : ""}
          </span>
          <span className="contact-status">{CONTACT_LABEL[entry.status](t)}</span>
        </div>
        <div className="contact-who">{who}</div>
        <div className="contact-text">{entry.text}</div>
        {entry.status === "answered" && entry.answer != null ? (
          <div className="contact-answer">
            <span className="muted">{t.youSaid}</span> {entry.answer}
          </div>
        ) : null}
        {entry.status === "open" ? (
          <form
            className="contact-reply"
            onSubmit={(event) => {
              event.preventDefault();
              if (answer.trim().length > 0 && !busy) void send({ answer: answer.trim() });
            }}
          >
            <input value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder={entry.mode === "question" ? t.answerPlaceholder : t.replyPlaceholder} aria-label={t.answerLabel(entry.contact.name)} maxLength={2000} />
            <button type="submit" className="primary" disabled={busy || answer.trim().length === 0}>
              {t.send}
            </button>
            <button type="button" className="quiet" disabled={busy} onClick={() => void send({ dismissed: true })}>
              {t.setAside}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

export type ResolveApproval = (approvalId: string, approved: boolean, allowRule?: string) => Promise<void>;

const STATUS_LABEL: Record<ApprovalChatEntry["status"], (t: Words) => string> = { pending: (t) => t.waitingForYou, approved: (t) => t.approvalApproved, denied: (t) => t.approvalDeclined, expired: (t) => t.approvalExpired };

/**
 * An approval card, as the prototype shows its Auto-review card in the chat:
 * what the agent wants to do, why it was flagged, the exact action, and the
 * user's two buttons. Once answered it stays as a record.
 */
function ApprovalCard({ entry, onResolve }: { readonly entry: ApprovalChatEntry; readonly onResolve: ResolveApproval }): React.JSX.Element {
  const t = useText(CHAT);
  const [busy, setBusy] = useState(false);
  const answer = async (approved: boolean, allowRule?: string): Promise<void> => {
    setBusy(true);
    try {
      await onResolve(entry.approvalId, approved, allowRule);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`bubble-row approval approval-${entry.status}`}>
      <div className="approval-card" title={timeOf(entry)}>
        <div className="approval-head">
          <span className="approval-label">{t.approvalNeeded}</span>
          <span className="approval-status">{STATUS_LABEL[entry.status](t)}</span>
        </div>
        <div className="approval-summary">{entry.summary}</div>
        <div className="approval-reason">{entry.reason}</div>
        {entry.detail != null && entry.detail.length > 0 ? <pre className="approval-detail">{entry.detail}</pre> : null}
        {entry.proposedRule != null ? (
          <div className="approval-rule">
            <span className="muted">{t.suggestedRule}</span> {entry.proposedRule}
          </div>
        ) : null}
        {entry.status === "pending" ? (
          <div className="approval-actions">
            <button type="button" className="primary" disabled={busy} onClick={() => void answer(true)}>
              {t.approve}
            </button>
            {entry.proposedRule != null ? (
              <button type="button" disabled={busy} onClick={() => void answer(true, entry.proposedRule)} title={t.approveAlwaysAbout}>
                {t.approveAlways}
              </button>
            ) : null}
            <button type="button" className="danger" disabled={busy} onClick={() => void answer(false)}>
              {t.decline}
            </button>
            <span className="approval-hint">{t.newMessageCancels}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type ResolveHandoff = (resolution: HandoffResolution) => Promise<void>;

const HANDOFF_LABEL: Record<HandoffChatEntry["status"], (t: Words) => string> = { waiting: (t) => t.handoffWaiting, handed_back: (t) => t.handoffDone, skipped: (t) => t.handoffSkipped, replied: (t) => t.handoffAnswered };

/**
 * The agent asking for the box, as the prototype's computer hand-off card:
 * the instruction, a status, and the three ways out — take the screen, say
 * you are done, or skip the step. It stays as a record once answered.
 */
function HandoffCard({ entry, onResolve, onTakeOver }: { readonly entry: HandoffChatEntry; readonly onResolve: ResolveHandoff; readonly onTakeOver: () => void }): React.JSX.Element {
  const t = useText(CHAT);
  const [busy, setBusy] = useState(false);
  const answer = async (resolution: HandoffResolution): Promise<void> => {
    setBusy(true);
    try {
      await onResolve(resolution);
    } finally {
      setBusy(false);
    }
  };
  const waiting = entry.status === "waiting";
  return (
    <div className={`bubble-row handoff handoff-${entry.status}`}>
      <div className="handoff-card" title={timeOf(entry)}>
        <div className="handoff-head">
          <span className="handoff-label">{t.computer}</span>
          <span className="handoff-status">{HANDOFF_LABEL[entry.status](t)}</span>
        </div>
        <div className="handoff-instruction">{entry.instruction}</div>
        {waiting ? (
          <div className="handoff-actions">
            <button type="button" className="primary" onClick={onTakeOver} title={t.takeOverAbout}>
              {t.takeOver}
            </button>
            <button type="button" disabled={busy} onClick={() => void answer("handed_back")}>
              {t.imDone}
            </button>
            <button type="button" disabled={busy} onClick={() => void answer("skipped")} title={t.skipAbout}>
              {t.skip}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusLine({ status, agentName }: { readonly status: AgentStatus; readonly agentName: string }): React.JSX.Element | null {
  // In a group chat the work is a member's, so the line names the member; the room is never the one thinking.
  const t = useText(CHAT);
  const who = status.speaker ?? agentName;
  if (status.awaitingApproval) return <div className="status-line" aria-live="polite">{t.waitingForApproval(who)}</div>;
  if (status.composing) {
    return (
      <div className="status-line typing" aria-live="polite">
        <span className="dots">
          <i />
          <i />
          <i />
        </span>
        {t.typing(who)}
      </div>
    );
  }
  if (status.activity != null) return <div className="status-line" aria-live="polite">{t.busyWith(who, status.activity)}</div>;
  if (status.working) return <div className="status-line" aria-live="polite">{t.thinking(who)}</div>;
  return null;
}

/**
 * One agent writing to another. It is not the user's conversation, so it is
 * not a bubble on either side: a card in the middle of the chat, named as
 * between bots, saying which way it went and who was at the other end, each
 * with their face. The same message is shown in both chats. A run of them
 * between the same two bots reads as one exchange, labelled once.
 */
function PeerCard({ entry, previous, agentId, agentName, avatarFor }: { readonly entry: PeerChatEntry; readonly previous: ChatEntry | undefined; readonly agentId: string; readonly agentName: string; readonly avatarFor: AvatarFor }): React.JSX.Element {
  const self = { id: agentId, name: agentName };
  const [from, to] = entry.direction === "out" ? [self, entry.peer] : [entry.peer, self];
  const joined = previous?.kind === "peer" && previous.peer.id === entry.peer.id;
  const t = useText(CHAT);
  return (
    <div className={`bubble-row peer peer-${entry.direction}${joined ? " joined" : ""}`}>
      <div className="peer-card" title={timeOf(entry)}>
        <div className="peer-head">
          <span className="peer-who">
            {avatarFor(from.id, from.name, 18)}
            <span className="peer-name">{from.name}</span>
          </span>
          <span className="peer-arrow" aria-label={t.peerTo}>
            →
          </span>
          <span className="peer-who">
            {avatarFor(to.id, to.name, 18)}
            <span className="peer-name">{to.name}</span>
          </span>
          {joined ? null : <span className="peer-label">{t.betweenBots}</span>}
        </div>
        <div className="peer-text">{entry.text}</div>
      </div>
    </div>
  );
}

/**
 * A member speaking in a group chat. Several agents share the room, so every
 * message carries its author's face and, for the first of a run, their name.
 */
function RoomBubble({ entry, avatarFor, previous, threading }: { readonly entry: RoomChatEntry; readonly avatarFor: AvatarFor; readonly previous: ChatEntry | undefined; readonly threading: Threading }): React.JSX.Element {
  const first = previous == null || previous.kind !== "room" || previous.author.id !== entry.author.id;
  const onMenu = threading.onMenu;
  const open = onMenu == null ? undefined : (x: number, y: number) => onMenu(entry, x, y);
  const reply = open == null ? null : (threading.replyFor?.(entry) ?? null);
  const press = useMessagePress(open, reply);
  return (
    <div className={`bubble-row agent room${first ? " first" : ""}`} {...press}>
      {avatarFor(entry.author.id, entry.author.name)}
      <div className="bubble agent" title={timeOf(entry)}>
        {first ? <div className="room-author">{entry.author.name}</div> : null}
        <div className="bubble-text markdown">
          <Markdown>{entry.text}</Markdown>
        </div>
      </div>
      {reply != null ? <SwipeReply /> : null}
      {open != null ? <MessageActions entry={entry} onMenu={open} onReply={reply} /> : null}
    </div>
  );
}

/**
 * A message the user sent that the host has not shown back yet: their own
 * bubble, greyed while it is on its way. One the box would not take says so
 * under it, to be sent again or let go.
 */
function OutgoingBubble({ message, mentionables, avatarFor, onView, onOpenAgent, onRetry, onDiscard, threading }: { readonly message: Outgoing; readonly mentionables: readonly Mentionable[]; readonly avatarFor: AvatarFor; readonly onView: ViewPicture; readonly onOpenAgent: OpenAgent; readonly onRetry?: ((id: string) => void) | undefined; readonly onDiscard?: ((id: string) => void) | undefined; readonly threading: Threading }): React.JSX.Element {
  const t = useText(CHAT);
  return (
    <div className={`chat-item outgoing outgoing-${message.status}`} aria-busy={message.status === "sending"}>
      <UserBubble entry={asEntry(message, Date.now())} mentionables={mentionables} avatarFor={avatarFor} onView={onView} onOpenAgent={onOpenAgent} threading={{ ...threading, onMenu: undefined, replyFor: undefined }} />
      {message.status === "failed" ? (
        <div className="outgoing-note" role="alert">
          <span title={message.error}>{t.notSent}</span>
          {onRetry != null ? (
            <button type="button" className="linkish" onClick={() => onRetry(message.id)}>
              {t.tryAgain}
            </button>
          ) : null}
          {onDiscard != null ? (
            <button type="button" className="linkish" onClick={() => onDiscard(message.id)}>
              {t.remove}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  readonly messages: readonly ChatEntry[];
  /** The user's messages on their way to the box (outbox.ts), drawn after the rest. */
  readonly outgoing?: readonly Outgoing[];
  readonly onRetrySend?: ((id: string) => void) | undefined;
  readonly onDiscardSend?: ((id: string) => void) | undefined;
  readonly status: AgentStatus;
  readonly agentId: string;
  readonly agentName: string;
  readonly avatarFor: AvatarFor;
  /** Who an @ can name in this chat; the user's bubbles pick those mentions out. */
  readonly mentionables: readonly Mentionable[];
  /** A mention in a bubble was clicked: open that agent's chat. */
  readonly onOpenAgent: OpenAgent;
  /** Older bubbles exist above what is shown; scrolling to the top asks for them. */
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onLoadMore: () => void;
  readonly onSubmitForm: (formId: string, values: Record<string, string>) => Promise<void>;
  readonly onCancelForm: (formId: string) => Promise<void>;
  readonly onSubmitLogin?: (loginId: string, request: SubmitLoginRequest) => Promise<void>;
  readonly onResolve: ResolveApproval;
  readonly onResolveHandoff: ResolveHandoff;
  readonly onTakeOver: () => void;
  /** Answering, or setting aside, what the agent passed on from WhatsApp (§6.19). */
  readonly onAnswerContact: AnswerContact;
  /** Reply to one message: the composer shows it as the target of the next message. */
  readonly onReply?: ReplyTo | undefined;
}

const NOTHING_OUTGOING: readonly Outgoing[] = [];

/** This close to the top, the page above is asked for. */
const LOAD_MORE_WITHIN_PX = 120;
/** How long the arrow and the picture take to leave; the CSS exit animations are this long. */
const EXIT_MS = 200;
/** How long "Copied" stays at the bottom of the chat. */
const COPIED_MS = 1400;
/** How long a message a quote led to stays lit. */
const LIT_MS = 1600;

/**
 * The chat as the user sees it: bubbles from the chat entries, a time stamp
 * between runs the way the prototype places them, and a status line while
 * the agent works. It opens at the newest message and stays there while new
 * ones arrive, unless the user has scrolled up to read; then an arrow at the
 * bottom right takes them back down, with a dot on it once something new has
 * landed below, as WhatsApp's does. Scrolling to the top reads the page
 * above, and the view does not jump when it lands. A picture opens large
 * over the page. The parent keys this on the agent, so a switch starts over.
 */
export function Chat({ messages, outgoing = NOTHING_OUTGOING, onRetrySend, onDiscardSend, status, agentId, agentName, avatarFor, mentionables, onOpenAgent, hasMore, loadingMore, onLoadMore, onSubmitForm, onCancelForm, onSubmitLogin, onResolve, onResolveHandoff, onTakeOver, onAnswerContact, onReply }: Props): React.JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  /** Reading the latest, as a ref for the layout effects and as state for the arrow. */
  const pinned = useRef(true);
  const [reading, setReading] = useState(true);
  /** Something landed below while the user was reading above. */
  const [unread, setUnread] = useState(false);
  const count = useRef(messages.length);
  /** How many of the user's messages were on their way at the last paint: one more is one just sent. */
  const sent = useRef(outgoing.length);
  /** The arrow was pressed and the view is gliding down: the scroll events on the way are not the user scrolling up. */
  const jumping = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [picture, setPicture] = useState<Picture | null>(null);
  const arrow = usePresence(!reading, EXIT_MS);
  const viewer = usePresence(picture != null, EXIT_MS);
  useBackClose(picture != null, () => setPicture(null));
  /** The scroll height and top the moment an older page was asked for, so the same bubble stays under the eye once it lands. */
  const anchor = useRef<{ height: number; top: number } | null>(null);
  const now = Date.now();
  const { zone } = useTimeZone();
  const t = useText(CHAT);
  const firstId = messages[0]?.id ?? null;

  /** A message's open menu, and a word at the bottom once something was copied. */
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedToast = usePresence(copied, EXIT_MS);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  /** The message a quote was pressed for, lit a moment once it is scrolled to. */
  const [lit, setLit] = useState<string | null>(null);
  useEffect(() => {
    if (lit == null) return;
    const timer = setTimeout(() => setLit(null), LIT_MS);
    return () => clearTimeout(timer);
  }, [lit]);

  const copy = (value: string): void => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  /** Answering one message, the user's own, the bot's, or a group member's; null for what cannot be answered. */
  const replyFor = (entry: ChatEntry): (() => void) | null => {
    const quote = findQuote(messages, entry.id);
    if (onReply == null || quote == null) return null;
    const author = quote.from === "user" ? t.you : (quote.author ?? agentName);
    return () => onReply({ id: entry.id, author, preview: quoteWords(quote, t) });
  };

  const openMenu = (entry: ChatEntry, x: number, y: number): void => {
    const words = copyTextOf(entry);
    const reply = replyFor(entry);
    const items: Array<MenuItem | Divider> = [];
    if (reply != null) items.push({ label: t.reply, icon: "reply", onSelect: reply });
    if (words != null) items.push({ label: t.copyText, icon: "copy", onSelect: () => copy(words) });
    if (items.length === 0) return;
    setMenu({ x, y, items, heading: displayDateTime(entry.createdAt), label: t.messageOptions });
  };

  /** Scrolls the quoted message into the middle of the view and lights it, as WhatsApp does. */
  const jumpTo = (id: string): void => {
    const element = container.current?.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(id)}"]`);
    if (element == null) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setLit(id);
  };

  const threading: Threading = { quoteFor: (id) => findQuote(messages, id), agentName, onJump: jumpTo, onMenu: openMenu, replyFor };

  // A page landed above: keep the bubble the user was looking at where it was.
  useLayoutEffect(() => {
    const element = container.current;
    if (element == null || anchor.current == null) return;
    element.scrollTop = anchor.current.top + (element.scrollHeight - anchor.current.height);
    anchor.current = null;
  }, [firstId]);

  // Something new, or the first paint: down to the latest, unless the user has scrolled up to read.
  useLayoutEffect(() => {
    const element = container.current;
    const sending = outgoing.length > sent.current;
    sent.current = outgoing.length;
    if (element == null || anchor.current != null) {
      count.current = messages.length;
      return;
    }
    const landed = messages.length > count.current;
    count.current = messages.length;
    const last = messages[messages.length - 1];
    // The user's own message, just sent, is theirs wherever they were reading.
    if (sending || (landed && last != null && followsArrival(pinned.current, last.kind))) {
      pinned.current = true;
      setReading(true);
      setUnread(false);
    } else if (landed && last != null) {
      setUnread(true);
    }
    if (pinned.current) element.scrollTop = element.scrollHeight;
  }, [messages, outgoing, status]);

  // Pictures and markdown settle after the first paint and make the content taller; stay at the bottom through that.
  useEffect(() => {
    const element = container.current;
    if (element == null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pinned.current && anchor.current == null) element.scrollTop = element.scrollHeight;
    });
    for (const child of element.children) observer.observe(child);
    return () => observer.disconnect();
  }, [messages]);

  const settle = (element: HTMLDivElement): void => {
    pinned.current = nearBottom(element);
    setReading(pinned.current);
    if (pinned.current) setUnread(false);
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const element = event.currentTarget;
    if (jumping.current != null) {
      if (!nearBottom(element)) return;
      clearTimeout(jumping.current);
      jumping.current = null;
    }
    settle(element);
    if (hasMore && !loadingMore && anchor.current == null && element.scrollTop < LOAD_MORE_WITHIN_PX) {
      anchor.current = { height: element.scrollHeight, top: element.scrollTop };
      onLoadMore();
    }
  };

  const toLatest = (): void => {
    const element = container.current;
    if (element == null) return;
    pinned.current = true;
    setReading(true);
    setUnread(false);
    if (jumping.current != null) clearTimeout(jumping.current);
    // A glide the user interrupts by scrolling never reaches the bottom; after a moment the view is read as it is.
    jumping.current = setTimeout(() => {
      jumping.current = null;
      if (container.current != null) settle(container.current);
    }, 800);
    element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  };

  return (
    <div className="chat-wrap">
      <div className="chat" ref={container} onScroll={onScroll}>
        {hasMore || loadingMore ? <div className="chat-more muted">{loadingMore ? t.loadingEarlier : t.scrollForEarlier}</div> : null}
        {messages.length === 0 && outgoing.length === 0 && !status.working && !hasMore ? <div className="empty">{t.sayHello}</div> : null}
        {messages.map((entry, index) => {
          const previous = messages[index - 1];
          const stamp = stampBefore(previous, entry, now, formatLocale(), zone);
          const bubble =
            entry.kind === "user" ? (
              <UserBubble threading={threading} entry={entry} mentionables={mentionables} avatarFor={avatarFor} onView={setPicture} onOpenAgent={onOpenAgent} />
            ) : entry.kind === "approval" ? (
              <ApprovalCard entry={entry} onResolve={onResolve} />
            ) : entry.kind === "handoff" ? (
              <HandoffCard entry={entry} onResolve={onResolveHandoff} onTakeOver={onTakeOver} />
            ) : entry.kind === "peer" ? (
              <PeerCard entry={entry} previous={previous} agentId={agentId} agentName={agentName} avatarFor={avatarFor} />
            ) : entry.kind === "form" ? (
              <FormCard entry={entry} onSubmit={onSubmitForm} onCancel={onCancelForm} />
            ) : entry.kind === "login" ? (
              <LoginCard entry={entry} onSubmit={onSubmitLogin ?? (async () => { throw new Error("Sign-in is unavailable here."); })} />
            ) : entry.kind === "room" ? (
              <RoomBubble entry={entry} avatarFor={avatarFor} previous={previous} threading={threading} />
            ) : entry.kind === "contact" ? (
              <ContactCard entry={entry} onAnswer={onAnswerContact} />
            ) : entry.kind === "notice" ? (
              <div className="chat-notice" role="status">
                {entry.text}
              </div>
            ) : (
              <AgentBubble threading={threading} entry={entry} previous={previous} onView={setPicture} />
            );
          return (
            <div key={entry.id} className={`chat-item${lit === entry.id ? " lit" : ""}`} data-entry-id={entry.id}>
              {stamp != null ? <div className="stamp">{stamp}</div> : null}
              {bubble}
            </div>
          );
        })}
        {outgoing.map((message) => (
          <OutgoingBubble key={message.id} message={message} mentionables={mentionables} avatarFor={avatarFor} onView={setPicture} onOpenAgent={onOpenAgent} onRetry={onRetrySend} onDiscard={onDiscardSend} threading={threading} />
        ))}
        <StatusLine status={status} agentName={agentName} />
      </div>
      {arrow.mounted ? (
        <button type="button" className={`jump-latest${arrow.closing ? " closing" : ""}`} onClick={toLatest} aria-label={unread ? t.jumpUnreadLabel : t.jumpLabel} title={unread ? t.jumpUnreadTitle : t.jumpTitle}>
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
            <path d="M10 4v12m0 0-5-5m5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {unread ? <span className="jump-dot" aria-hidden="true" /> : null}
        </button>
      ) : null}
      {menu != null ? <ContextMenu menu={menu} onClose={() => setMenu(null)} /> : null}
      {copiedToast.mounted ? (
        <div className={`toast${copiedToast.closing ? " closing" : ""}`} role="status" aria-live="polite">
          {t.copied}
        </div>
      ) : null}
      {viewer.mounted && picture != null ? <Lightbox src={picture.src} alt={picture.alt} {...(picture.name == null ? {} : { name: picture.name })} closing={viewer.closing} onClose={() => setPicture(null)} /> : null}
    </div>
  );
}
